import React, { useState } from 'react';
import UserAvatar from './UserAvatar';

/**
 * Avatar component with fallback support
 * @param {string} src - Image URL
 * @param {string} alt - Alt text (usually person's name)
 * @param {string} size - Size class (default: w-10 h-10)
 * @param {string} className - Additional CSS classes
 */
export default function Avatar({ src, alt = 'User', size = 'w-10 h-10', className = '' }) {
  const [imgError, setImgError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Generate initials from name
  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Generate a consistent color based on the name
  const getAvatarColor = (name) => {
    if (!name) return 'bg-purple';

    const colors = [
      'bg-blue-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-cyan-500',
      'bg-teal-500',
      'bg-emerald-500',
      'bg-violet-500'
    ];

    const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[index % colors.length];
  };

  const handleImageError = () => {
    setImgError(true);
    setIsLoading(false);
  };

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  // If no src or image failed to load, show fallback avatar
  if (!src || imgError) {
    return <UserAvatar className={`${size} ${className}`} />;
  }

  return (
    <div className={`${size} rounded-full overflow-hidden ${className} relative`}>
      {isLoading && (
        <div className={`absolute inset-0 ${getAvatarColor(alt)} animate-pulse`}></div>
      )}
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        onError={handleImageError}
        onLoad={handleImageLoad}
      />
    </div>
  );
}

